import { normalizeLoadingFigure } from '../../constants/loadingFigures'
import { normalizeViewPrefs } from '../../utils/viewPrefs'
import { normalizeCharacterKeys } from '../../utils/characterKeys'
import { normalizeThemeSettings } from '../../services/theme/themeEngine'
import { defaultPageNamingSchemes, type PageNamingScheme } from '../../services/pages/pageNaming'
import { normalizeDocumentTemplates } from '../../services/corpus/documentTemplates'
import { normalizePageStatuses } from '../../services/pages/pageStatus'
import { normalizePageViewDisplay } from '../../utils/pageViewDisplay'
import { normalizeListPrefs } from '../../utils/listStyles'
import { TYPEWRITER_MOTIONS, type TypewriterMotion } from '../../utils/typewriterMotion'
import { isCaretBlink, isCaretShape } from '../../utils/caretStyle'
import { defaultBarLayout, migrateBarLayout, normalizeBarLayout, type BarDevice, type BarLayout, type LegacyBarSettings } from '../../utils/barLayout'
import { normalizeCorpusView } from '../../utils/corpusViewChoice'
import { normalizeGoalDays } from '../../utils/goalModel'
import { normalizeCalendarPrefs, normalizeDocketPrefs, normalizeGoalPrefs, normalizeToolPrefs } from '../../utils/toolSettings'
import { defaults, HYPHENATIONS, MOBILE_RIGHT_EDGES, TABS_VISIBILITIES, TAB_STYLES, type Hyphenation, type MobileRightEdge, type TabOpenMode, type TabStyle, type TabsVisibility, type ToolbarStyle } from './defaults'
import { normalizeWritingHabit } from './writingHabit'
import { normalizeLooseEndSettings } from './looseEnds'
import { normalizeEditorModeSettings } from './editorMode'
import { normalizeProofingSettings } from './proofing'
import { normalizeShortcutOverrides } from './normalize'
import { deepMergeTypography } from './typography'
import { deepMergeIconStyles } from './iconStyles'
import { deepMergeContextMenuItems } from './contextMenu'

// Reading the saved settings at startup, cleaning up each value and carrying
// over what older saves kept under other names.

const STORAGE_KEY = 'ink-settings'

export function loadPersisted(): any {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

export function savePersisted(values: Record<string, any>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(values))
}

/** A text width and its unit, cleaned up on load. */
function normalizeWidth(rawUnit: unknown, rawWidth: unknown): { unit: 'px' | 'ch'; width: number } {
  const unit: 'px' | 'ch' = rawUnit === 'px' || rawUnit === 'ch'
    ? rawUnit
    : (typeof rawWidth === 'number' && rawWidth > 200 ? 'px' : 'ch')
  let width: number
  if (typeof rawWidth !== 'number') {
    width = unit === 'px' ? 700 : 65
  } else if (unit === 'ch') {
    width = rawWidth > 200 || rawWidth < 20 ? 65 : rawWidth
  } else {
    width = rawWidth < 200 || rawWidth > 3000 ? 700 : rawWidth
  }
  return { unit, width }
}

function uniqueStrings(raw: unknown): string[] {
  return Array.isArray(raw)
    ? [...new Set<string>(raw.filter((item: unknown): item is string => typeof item === 'string'))]
    : []
}

// The toolbar settings from before keys could move. They are no longer kept;
// a saved, synced or imported settings blob that still has them seeds the bar
// layouts once, when it has none of its own.
export const LEGACY_BAR_KEYS = [
  'toolbar', 'rightEdgeMode', 'rightBarItems', 'mobileKeyboardToolbarItems',
  'mobileStaticTopToolbarItems', 'mobileRightBarItems', 'selectionHighlightItems', 'selectionEmptyItems',
]

export function legacyBarSettings(raw: Record<string, any>): LegacyBarSettings {
  return {
    toolbar: raw.toolbar,
    // A saved setup with no right-edge choice was showing the right toolbar, then the default.
    rightEdgeMode: raw.rightEdgeMode ?? 'toolbar',
    rightBarItems: raw.rightBarItems,
    toolOrder: raw.toolPrefs?.order,
    // Both were switches of their own before they became keys.
    goalReadoutShown: raw.goalPrefs?.readout?.show,
    looseEndCountShown: raw.looseEnds?.bottomBarCount,
    mobileKeyboardToolbarItems: raw.mobileKeyboardToolbarItems,
    mobileStaticTopToolbarItems: raw.mobileStaticTopToolbarItems,
    mobileRightBarItems: raw.mobileRightBarItems,
    selectionHighlightItems: raw.selectionHighlightItems,
    selectionEmptyItems: raw.selectionEmptyItems,
  }
}

// Which keys sit on which bar, one layout per app (see utils/barLayout).
// A saved setup from before keys could move is carried over as it looked.
function initialBarLayout(persisted: Record<string, any>, device: BarDevice): BarLayout {
  const saved = persisted[device === 'desktop' ? 'desktopBarLayout' : 'phoneBarLayout']
  if (saved) return normalizeBarLayout(saved, device)
  if (!Object.keys(persisted).length) return defaultBarLayout(device)
  return migrateBarLayout(device, legacyBarSettings(persisted))
}

/** Every synced setting's starting value, from what was saved on this device. */
export function readSettings(persisted: Record<string, any>) {
  const merged: Record<string, any> = { ...defaults, ...persisted }

  // Text size, text width and typewriter mode are kept per device (see the
  // store). Older saves held one shared value, which carries over as the
  // desktop copy.
  const desktopWidth = normalizeWidth(persisted.desktopWidthUnit ?? persisted.widthUnit, persisted.desktopWidth ?? persisted.width)
  const mobileWidth = normalizeWidth(merged.mobileWidthUnit, merged.mobileWidth)

  // A writer who used the right toolbar keeps its size; everyone else gets the taskbar's.
  // (A saved setup with no right-edge choice was showing the right toolbar, then the default.)
  const usedRightToolbar = Object.keys(persisted).length > 0 && (persisted.rightEdgeMode ?? 'toolbar') === 'toolbar'

  return {
    desktopWidth: desktopWidth.width,
    desktopWidthUnit: desktopWidth.unit,
    mobileWidth: mobileWidth.width,
    mobileWidthUnit: mobileWidth.unit,
    desktopFontSize: (persisted.desktopFontSize ?? persisted.fontSize ?? 20) as number,
    mobileFontSize: (merged.mobileFontSize ?? 18) as number,
    desktopCorpusView: normalizeCorpusView(merged.desktopCorpusView),
    mobileCorpusView: normalizeCorpusView(merged.mobileCorpusView),
    fontFamily: (merged.fontFamily || "'Cutive', Georgia, serif") as string,
    installedFontIds: uniqueStrings(merged.installedFontIds),
    appFontOrder: uniqueStrings(merged.appFontOrder),
    hiddenAppFonts: uniqueStrings(merged.hiddenAppFonts),
    showSystemFonts: merged.showSystemFonts !== undefined ? !!merged.showSystemFonts : true,
    lineHeight: (merged.lineHeight || 1.6) as number,
    hyphenation: (HYPHENATIONS.includes(merged.hyphenation) ? merged.hyphenation : 'off') as Hyphenation,
    bgColor: (merged.bgColor || '#1e1e1e') as string,
    textColor: (merged.textColor || '#e0e0e0') as string,
    startupBehavior: (merged.startupBehavior || 'last-page') as 'last-page' | 'new-page',
    exportAuthorName: typeof merged.exportAuthorName === 'string' ? merged.exportAuthorName as string : '',
    newPagePlaceholder: (merged.newPagePlaceholder !== undefined ? merged.newPagePlaceholder : 'Start writing…') as string,
    newPagePlaceholderEnabled: merged.newPagePlaceholderEnabled !== undefined ? !!merged.newPagePlaceholderEnabled : true,
    pageNamingSchemes: (
      Array.isArray(merged.pageNamingSchemes)
        ? merged.pageNamingSchemes.filter((item: any) => item && typeof item.id === 'string' && typeof item.name === 'string' && typeof item.template === 'string')
        : structuredClone(defaultPageNamingSchemes)
    ) as PageNamingScheme[],
    pageNamingAssignments: (
      merged.pageNamingAssignments && typeof merged.pageNamingAssignments === 'object'
        ? { ...merged.pageNamingAssignments }
        : {}
    ) as Record<string, string>,
    documentTemplates: normalizeDocumentTemplates(merged.documentTemplates),
    referenceHighlightingEnabled: !!merged.referenceHighlightingEnabled,
    panelMode: merged.panelMode as 'button' | 'edge',
    hoverTopEnabled: merged.hoverTopEnabled !== false,
    hoverBottomEnabled: merged.hoverBottomEnabled !== false,
    hoverLeftEnabled: merged.hoverLeftEnabled !== false,
    hoverRightEnabled: merged.hoverRightEnabled !== false,
    cloudSyncUrl: (merged.cloudSyncUrl || '') as string,
    cloudSyncApiKey: (merged.cloudSyncApiKey || '') as string,
    cloudSyncAutoSync: !!merged.cloudSyncAutoSync,
    spellcheckEnabled: !!merged.spellcheckEnabled,
    proofing: normalizeProofingSettings(merged.proofing),
    viewPrefs: normalizeViewPrefs(merged.viewPrefs),
    shortcutOverrides: normalizeShortcutOverrides(merged.shortcutOverrides),
    characterKeys: normalizeCharacterKeys(merged.characterKeys),
    syncSettingsEnabled: !!merged.syncSettingsEnabled,
    keyLockPromptStyle: (merged.keyLockPromptStyle || 'modal') as 'modal' | 'indicator',
    loadingFigure: normalizeLoadingFigure(merged.loadingFigure),
    colorPickerMode: (merged.colorPickerMode || 'basic') as 'basic' | 'advanced',
    wordCountDisplay: (merged.wordCountDisplay === 'exact' ? 'exact' : 'abbreviated') as 'abbreviated' | 'exact',
    scrollbarWidth: Number.isFinite(merged.scrollbarWidth) ? Math.min(24, Math.max(6, Math.round(merged.scrollbarWidth))) : 12,
    writingHabit: normalizeWritingHabit(merged.writingHabit),
    goalDays: normalizeGoalDays(merged.goalDays),
    calendarPrefs: normalizeCalendarPrefs(persisted.calendarPrefs ?? {
      // Carry over the two calendar switches kept before the Calendar page.
      layers: { goalDots: persisted.calendarShowGoals === true, tasks: persisted.calendarShowTasks !== false },
    }),
    goalPrefs: normalizeGoalPrefs(persisted.goalPrefs ?? {
      // The sprint length was kept on each device before Settings had a Goals page.
      sprint: { defaultMinutes: Number(localStorage.getItem('ink-sprint-minutes')) || undefined },
    }),
    docketPrefs: normalizeDocketPrefs(persisted.docketPrefs),
    looseEnds: normalizeLooseEndSettings(merged.looseEnds),
    toolPrefs: normalizeToolPrefs(merged.toolPrefs),
    editorMode: normalizeEditorModeSettings(merged.editorMode),
    pageViewDisplay: normalizePageViewDisplay(merged.pageViewDisplay),
    listPrefs: normalizeListPrefs(merged.listPrefs),
    pageStatuses: normalizePageStatuses(merged.pageStatuses),
    theme: normalizeThemeSettings(merged.theme),
    mobileLayout: (merged.mobileLayout || 'layout2') as string,
    typography: deepMergeTypography(persisted.typography),
    iconStyles: deepMergeIconStyles(persisted.iconStyles),
    mobileTopPadding: (merged.mobileTopPadding !== undefined ? merged.mobileTopPadding : 15) as number,
    rightBarFontWeights: (
      Array.isArray(merged.rightBarFontWeights)
        ? [...new Set<number>(merged.rightBarFontWeights
            .map(Number)
            .filter((weight: number) => Number.isInteger(weight) && weight >= 1 && weight <= 1000))]
            .sort((a, b) => a - b)
        : [...defaults.rightBarFontWeights]
    ) as number[],
    selectionToolbarDisplayMode: (merged.selectionToolbarDisplayMode || 'icons') as string,
    mobileKeyboardToolbarEnabled: merged.mobileKeyboardToolbarEnabled !== undefined ? !!merged.mobileKeyboardToolbarEnabled : true,
    // Older setups had an on/off switch for the side bar.
    mobileRightEdge: (
      MOBILE_RIGHT_EDGES.includes(persisted.mobileRightEdge) ? persisted.mobileRightEdge
        : persisted.mobileRightBarEnabled === false ? 'off' : 'bar'
    ) as MobileRightEdge,
    mobileSelectionToolbarEnabled: merged.mobileSelectionToolbarEnabled !== undefined ? !!merged.mobileSelectionToolbarEnabled : true,
    radialMenuEnabled: !!merged.radialMenuEnabled,
    mobileStaticTopToolbarEnabled: !!merged.mobileStaticTopToolbarEnabled,
    radialMenuSelectionItems: (merged.radialMenuSelectionItems || []) as string[],
    radialMenuEmptyItems: (merged.radialMenuEmptyItems || []) as string[],
    desktopTypewriterModeEnabled: !!(persisted.desktopTypewriterModeEnabled ?? persisted.typewriterModeEnabled),
    mobileTypewriterModeEnabled: !!merged.mobileTypewriterModeEnabled,
    desktopTypewriterEyeline: (merged.desktopTypewriterEyeline !== undefined ? merged.desktopTypewriterEyeline : 50) as number,
    desktopTypewriterThreshold: (merged.desktopTypewriterThreshold !== undefined ? merged.desktopTypewriterThreshold : 2) as number,
    mobileTypewriterEyeline: (merged.mobileTypewriterEyeline !== undefined ? merged.mobileTypewriterEyeline : 40) as number,
    mobileTypewriterThreshold: (merged.mobileTypewriterThreshold !== undefined ? merged.mobileTypewriterThreshold : 2) as number,
    typewriterMotion: (TYPEWRITER_MOTIONS.includes(merged.typewriterMotion) ? merged.typewriterMotion : 'drift') as TypewriterMotion,
    typewriterBailEnabled: !!merged.typewriterBailEnabled,
    caretShape: isCaretShape(merged.caretShape) ? merged.caretShape : 'bar',
    caretBlink: isCaretBlink(merged.caretBlink) ? merged.caretBlink : 'steady',
    caretHoldWhileTyping: !!merged.caretHoldWhileTyping,
    caretRestWhenIdle: !!merged.caretRestWhenIdle,
    clickToTypeEnabled: merged.clickToTypeEnabled !== undefined ? !!merged.clickToTypeEnabled : true,
    downArrowNewLineEnabled: merged.downArrowNewLineEnabled !== undefined ? !!merged.downArrowNewLineEnabled : true,
    rememberPlaceEnabled: merged.rememberPlaceEnabled !== undefined ? !!merged.rememberPlaceEnabled : true,
    tableHeaderRowEnabled: merged.tableHeaderRowEnabled !== undefined ? !!merged.tableHeaderRowEnabled : true,
    focusDimmingMode: (merged.focusDimmingMode || 'off') as 'off' | 'paragraph' | 'sentence' | 'word',
    documentSearchPosition: (merged.documentSearchPosition === 'above' ? 'above' : 'below') as 'above' | 'below',

    tabsVisibility: (TABS_VISIBILITIES.includes(merged.tabsVisibility) ? merged.tabsVisibility : 'never') as TabsVisibility,
    tabOpenMode: (merged.tabOpenMode === 'replace' ? 'replace' : 'new') as TabOpenMode,
    tabStyle: (TAB_STYLES.includes(merged.tabStyle) ? merged.tabStyle : 'above') as TabStyle,

    // Desktop toolbar dimensions
    toolbarStyle: (['guide', 'carriage', 'pill'].includes(merged.toolbarStyle) ? merged.toolbarStyle : 'guide') as ToolbarStyle,
    topBarRulerEnabled: merged.topBarRulerEnabled !== false,
    topBarThickness: (merged.topBarThickness ?? 46) as number,
    topBarIconSize: (merged.topBarIconSize ?? 28) as number,
    bottomBarThickness: (merged.bottomBarThickness ?? 46) as number,
    bottomBarIconSize: (merged.bottomBarIconSize ?? 28) as number,
    rightBarWidth: (persisted.rightBarWidth ?? (usedRightToolbar ? persisted.rightBarThickness ?? 46 : 62)) as number,
    rightBarKeySize: (persisted.rightBarKeySize ?? (usedRightToolbar ? persisted.rightBarIconSize ?? 28 : 44)) as number,

    // Mobile toolbar dimensions
    mobileKeyboardToolbarThickness: (merged.mobileKeyboardToolbarThickness ?? 46) as number,
    mobileKeyboardToolbarIconSize: (merged.mobileKeyboardToolbarIconSize ?? 32) as number,
    mobileStaticTopToolbarThickness: (merged.mobileStaticTopToolbarThickness ?? 52) as number,
    mobileStaticTopToolbarIconSize: (merged.mobileStaticTopToolbarIconSize ?? 36) as number,
    mobileRightBarThickness: (merged.mobileRightBarThickness ?? 68) as number,
    mobileRightBarIconSize: (merged.mobileRightBarIconSize ?? 44) as number,
    mobileSelectionToolbarThickness: (merged.mobileSelectionToolbarThickness ?? 42) as number,
    mobileSelectionToolbarIconSize: (merged.mobileSelectionToolbarIconSize ?? 38) as number,
    radialMenuRadius: (merged.radialMenuRadius ?? 100) as number,
    radialMenuButtonSize: (merged.radialMenuButtonSize ?? 48) as number,

    contextMenuEnabled: merged.contextMenuEnabled !== undefined ? !!merged.contextMenuEnabled : true,
    contextMenuItems: deepMergeContextMenuItems(persisted.contextMenuItems),
    desktopBarLayout: initialBarLayout(persisted, 'desktop'),
    phoneBarLayout: initialBarLayout(persisted, 'phone'),
  }
}

export type SettingsValues = ReturnType<typeof readSettings>
